import React from 'react';
import {compose, graphql} from 'react-apollo';
import gql from 'graphql-tag';
import {Button, Card, Input, Label, EqualColumns} from 'ui-kit';

import {FormFooter} from '../../../FormFooter';

import * as styles from './PayoutDestination.module.css';

class PayoutDestination extends React.Component {
  state = {
    formVisible: false,
    accountHolderName: '',
    accountHolderType: 'COMPANY', // INDIVIDUAL or COMPANY
    routingNumber: '',
    accountNumber: '',
    accountNumberConfirmation: '',
    acceptsTos: false,
    status: null,
    errors: {},
  };

  render() {
    const {payoutAccount} = this.props;
    const {
      formVisible,
      status,
      accountHolderName,
      accountNumber,
      accountNumberConfirmation,
      routingNumber,
      accountHolderType,
      errors,
      acceptsTos,
    } = this.state;

    let content;
    if (formVisible) {
      content = (
        <form method="post" onSubmit={this.handleAccountInfoSubmit}>
          <p>
            <strong>Add a payout account</strong> <br /> <br />
            We will make payouts to this account. It must be a checking account
            that accepts US dollars.
          </p>

          <div className={styles.AccountHolderType}>
            <Label>Account type</Label>
            <Label>
              <input
                type="radio"
                name="accountHolderType"
                value="COMPANY"
                checked={accountHolderType === 'COMPANY'}
                onChange={this.handleInputChange}
              />{' '}
              <span className={styles.AccountHolderTypeText}>
                This is a company account
              </span>
            </Label>
            <Label>
              <input
                type="radio"
                name="accountHolderType"
                value="INDIVIDUAL"
                checked={accountHolderType === 'INDIVIDUAL'}
                onChange={this.handleInputChange}
              />{' '}
              <span className={styles.AccountHolderTypeText}>
                This account belongs to an individual
              </span>
            </Label>
          </div>

          <EqualColumns>
            <Input
              label="Account holder name"
              value={accountHolderName}
              name="accountHolderName"
              onChange={this.handleInputChange}
              noPadding
            />

            <Input
              label="Routing number"
              value={routingNumber}
              name="routingNumber"
              onChange={this.handleInputChange}
              noPadding
            />
          </EqualColumns>

          <EqualColumns>
            <Input
              label="Account number"
              value={accountNumber}
              name="accountNumber"
              onChange={this.handleInputChange}
              noPadding
            />

            <Input
              label="Confirm account number"
              value={accountNumberConfirmation}
              name="accountNumberConfirmation"
              onChange={this.handleInputChange}
              errorMessage={errors.accountNumberConfirmation}
              noPadding
            />
          </EqualColumns>

          <EqualColumns columns={1}>
            <div>
              <Label>
                <input
                  type="checkbox"
                  name="acceptsTos"
                  onChange={event =>
                    this.setState({acceptsTos: event.target.checked})
                  }
                  value={acceptsTos}
                />{' '}
                <span className={styles.TermsText}>
                  I accept the platform terms, including the{' '}
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://stripe.com/connect-account/legal"
                  >
                    Stripe Connected Account Agreement
                  </a>
                </span>
              </Label>

              {errors.acceptsTos ? (
                <p style={{color: 'crimson'}}>
                  <small>You must accept the terms of service.</small>
                </p>
              ) : null}
            </div>
          </EqualColumns>

          <FormFooter status={status} onCancelClick={this.hideForm} />
        </form>
      );
    } else if (
      payoutAccount == null ||
      payoutAccount.routingNumber == null ||
      payoutAccount.last4 == null
    ) {
      content = (
        <div>
          <p className={styles.EmptyStateText}>
            <strong className={styles.NoAccountText}>
              You do not have an account connected as a payout destination.
            </strong>{' '}
            We will not be able to process any payouts for your account until
            you have entered banking information.
          </p>
          <Button onClick={this.showForm}>Add an account</Button>
        </div>
      );
    } else {
      const {routingNumber, last4, bankName} = payoutAccount;
      content = (
        <>
          <table className={styles.BankNumbers}>
            <tbody>
              <tr>
                <td className={styles.BankNumbersCell}>
                  <strong>Bank name</strong>
                </td>
                <td className={styles.BankNumbersCell}>{bankName}</td>
              </tr>
              <tr>
                <td className={styles.BankNumbersCell}>
                  <strong>Routing number</strong>
                </td>
                <td className={styles.BankNumbersCell}>{routingNumber}</td>
              </tr>
              <tr>
                <td className={styles.BankNumbersCell}>
                  <strong>Account number</strong>
                </td>
                <td className={styles.BankNumbersCell}>•••••{last4}</td>
              </tr>
            </tbody>
          </table>

          <Button color="light" onClick={this.showForm}>
            Change account
          </Button>
        </>
      );
    }

    return (
      <Card>
        <h3 className={styles.Heading}>Payout destination</h3>
        {content}
      </Card>
    );
  }

  showForm = () => {
    this.setState({formVisible: true});
  };

  hideForm = () => {
    this.setState({formVisible: false});
  };

  handleInputChange = event => {
    const {name, value} = event.target;

    this.setState({[name]: value});
  };

  handleAccountInfoSubmit = async event => {
    event.preventDefault();

    this.setState({status: 'loading'});

    const {
      accountHolderName,
      accountNumber,
      accountNumberConfirmation,
      routingNumber,
      accountHolderType,
      acceptsTos,
    } = this.state;
    const {mutate} = this.props;

    if (!acceptsTos) {
      this.setState({
        status: null,
        errors: {acceptsTos: true},
      });
      return;
    }

    if (accountNumber !== accountNumberConfirmation) {
      this.setState({
        status: null,
        errors: {accountNumberConfirmation: 'Must match account number'},
      });
      return;
    }

    try {
      const {data} = await mutate({
        variables: {
          input: {
            routingNumber,
            accountNumber,
            accountHolderName,
            accountHolderType,
            acceptsTos,
          },
        },
      });

      if (data.updatePracticePayoutsAccount.errors.length > 0) {
        console.error(data.updatePracticePayoutsAccount.errors);
      } else {
        this.setState({status: 'success'});

        window.setTimeout(() => {
          this.setState({
            formVisible: false,
            status: null,
          });
          this.props.onSuccess();
        }, 2000);
      }
    } catch (error) {}
  };
}

export default compose(
  graphql(
    gql`
      mutation UpdatePayoutsAccountMutation(
        $input: UpdatePracticePayoutsAccountInput!
      ) {
        updatePracticePayoutsAccount(input: $input) {
          errors {
            path
            message
          }
          practice {
            id
          }
        }
      }
    `,
  ),
)(PayoutDestination);
